import React, { useState, useEffect, useOptimistic } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, User, Mail, Phone, Building2, MapPin, Globe, UserPlus, Check, Clock, Calendar, Briefcase, Users, GraduationCap, Search, Home, Sparkles, Share2, ExternalLink, CalendarDays, UserCheck } from 'lucide-react';
import { Map, AdvancedMarker } from '@vis.gl/react-google-maps';
import { getPeopleByUuid, updatePeopleCoordinates, getProfileStats } from '../../services/apiService';
import { geocodeAddress } from '../../services/geocodingService';
import { getUserContacts, addPeopleAsContact, removeUserContact, isUserContact, getContactByPeopleId } from '../../services/userContactsService';
import { getPublicAvailability, DAYS_OF_WEEK } from '../../services/agendaService';
import { getProfilePhoto, getFileUrl } from '../../services/filesService';
import { getPeopleOccupations, getPeopleSkills, getSkillsForOccupation } from '../../services/skillsService';
import { getPlacesByPeopleId, PLACE_TYPES } from '../../services/placesService';

// Social share icons as SVG components
const FacebookIcon = () => (
    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
    </svg>
);

const XIcon = () => (
    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
    </svg>
);

const LinkedInIcon = () => (
    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
    </svg>
);

const WhatsAppIcon = () => (
    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
    </svg>
);

const TelegramIcon = () => (
    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
        <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
    </svg>
);

// Profile types mapping
const PROFILE_TYPES = {
    1: { code: 'STUDENT', labelFr: 'Étudiant', labelEn: 'Student', icon: GraduationCap, color: 'bg-blue-500' },
    3: { code: 'INDIVIDUAL', labelFr: 'Particulier', labelEn: 'Individual', icon: Home, color: 'bg-gray-500' },
    4: { code: 'ARTISAN', labelFr: 'Artisan', labelEn: 'Artisan', icon: Building2, color: 'bg-amber-500' },
    5: { code: 'PROFESSIONAL', labelFr: 'Professionnel', labelEn: 'Professional', icon: Briefcase, color: 'bg-indigo-500' },
    6: { code: 'RETIRED', labelFr: 'Pensionné', labelEn: 'Retired', icon: User, color: 'bg-teal-500' }
};

// Employment status mapping
const EMPLOYMENT_STATUS = {
    'JOBSEEKER': { labelFr: 'Demandeur d\'emploi', labelEn: 'Job Seeker', icon: Search, color: 'bg-green-500' },
    'EMPLOYER': { labelFr: 'Offreur d\'emploi', labelEn: 'Employer', icon: Briefcase, color: 'bg-orange-500' }
};

/**
 * Normalize people data to handle both lowercase and uppercase keys from ColdFusion
 */
const normalizePeople = (people) => ({
    id: people.id || people.ID,
    peopleUuid: people.peopleUuid || people.PEOPLEUUID || people.peopleuuid,
    firstName: people.firstName || people.FIRSTNAME || people.firstname || '',
    lastName: people.lastName || people.LASTNAME || people.lastname || '',
    email: people.email || people.EMAIL || '',
    phone: people.phone || people.PHONE || people.gsm || people.GSM || '',
    company: people.company || people.COMPANY || '',
    position: people.position || people.POSITION || '',
    // Use googleAddress as primary address field
    address: people.googleAddress || people.GOOGLEADDRESS || people.googleaddress || people.address || people.ADDRESS || '',
    website: people.website || people.WEBSITE || '',
    latitude: people.latitude || people.LATITUDE || null,
    longitude: people.longitude || people.LONGITUDE || null,
    role: (people.role || people.ROLE || 'PRIVATE').toUpperCase(),
    profileTypeId: people.profileTypeId || people.PROFILETYPEID || people.profiletypeid || null,
    employmentStatus: (people.employmentStatus || people.EMPLOYMENTSTATUS || people.employmentstatus || '').toUpperCase() || null,
    createdAt: people.createdAt || people.CREATEDAT || people.createdat,
    updatedAt: people.updatedAt || people.UPDATEDAT || people.updatedat
});

/**
 * People Detail Content Component
 * Content-only component that shows person details (without layout)
 */
const PeopleDetailContent = () => {
    const { uuid } = useParams();
    const navigate = useNavigate();
    const [people, setPeople] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [geocoding, setGeocoding] = useState(false);
    const [language] = useState('fr');
    const [availability, setAvailability] = useState([]);
    const [loadingAvailability, setLoadingAvailability] = useState(false);

    // New states for additional data
    const [profilePhotoUrl, setProfilePhotoUrl] = useState(null);
    const [occupations, setOccupations] = useState([]);
    const [occupationSkills, setOccupationSkills] = useState({});
    const [userSkillIds, setUserSkillIds] = useState([]);
    const [enterprises, setEnterprises] = useState([]);
    const [clubs, setClubs] = useState([]);
    const [profileStats, setProfileStats] = useState(null);

    // Get current user info - userId is the numeric ID
    const userId = parseInt(localStorage.getItem('userId') || '0', 10);
    const [userContacts, setUserContacts] = useState([]);
    const [contactsLoaded, setContactsLoaded] = useState(false);

    // React 19: useOptimistic for instant UI updates
    const [optimisticPeople, updateOptimisticPeople] = useOptimistic(
        people,
        (state, newData) => state ? { ...state, ...newData } : null
    );

    // Load user contacts on mount
    useEffect(() => {
        const loadContacts = async () => {
            if (userId > 0) {
                try {
                    const contacts = await getUserContacts(userId);
                    setUserContacts(contacts);
                } catch (err) {
                    console.error('Error loading user contacts:', err);
                }
            }
            setContactsLoaded(true);
        };
        loadContacts();
    }, [userId]);

    useEffect(() => {
        const loadPeople = async () => {
            setLoading(true);
            setError(null);
            try {
                const data = await getPeopleByUuid(uuid);
                const normalizedPeople = normalizePeople(data);
                setPeople(normalizedPeople);

                // If address exists but coordinates don't, geocode the address
                if (normalizedPeople.address && (!normalizedPeople.latitude || !normalizedPeople.longitude)) {
                    geocodePeopleAddress(normalizedPeople);
                }

                // Load availability for this person
                loadAvailability(normalizedPeople.peopleUuid);

                // Load additional data
                loadProfilePhoto(normalizedPeople.peopleUuid);
                loadOccupationsAndSkills(normalizedPeople.peopleUuid);
                loadPlaces(normalizedPeople.id);
                loadProfileStats(normalizedPeople.peopleUuid);
            } catch (err) {
                console.error('Error loading people:', err);
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        loadPeople();
    }, [uuid]);

    // Load profile photo
    const loadProfilePhoto = async (peopleUuid) => {
        try {
            const result = await getProfilePhoto(peopleUuid);
            const success = result.success ?? result.SUCCESS;
            const data = result.data ?? result.DATA;

            if (success && data) {
                const filePath = data.filePath ?? data.FILEPATH ?? data.filepath;
                if (filePath) {
                    setProfilePhotoUrl(getFileUrl(filePath));
                }
            }
        } catch (err) {
            console.error('Error loading profile photo:', err);
        }
    };

    // Load occupations and their skills
    const loadOccupationsAndSkills = async (peopleUuid) => {
        try {
            // Load occupations
            const occupationsData = await getPeopleOccupations(peopleUuid, language);
            setOccupations(occupationsData);

            // Load user's selected skill IDs
            const skillsData = await getPeopleSkills(peopleUuid, null, language);
            setUserSkillIds(skillsData.skillIds || []);

            // Load skills for each occupation
            const skillsMap = {};
            for (const occ of occupationsData) {
                const skills = await getSkillsForOccupation(occ.id, language);
                skillsMap[occ.id] = skills;
            }
            setOccupationSkills(skillsMap);
        } catch (err) {
            console.error('Error loading occupations/skills:', err);
        }
    };

    // Load places (enterprises and clubs)
    const loadPlaces = async (peopleId) => {
        try {
            const enterprisesData = await getPlacesByPeopleId(peopleId, PLACE_TYPES.ENTERPRISE);
            setEnterprises(enterprisesData || []);

            const clubsData = await getPlacesByPeopleId(peopleId, PLACE_TYPES.CLUB);
            setClubs(clubsData || []);
        } catch (err) {
            console.error('Error loading places:', err);
        }
    };

    // Load profile statistics
    const loadProfileStats = async (peopleUuid) => {
        try {
            const stats = await getProfileStats(peopleUuid);
            setProfileStats(stats);
        } catch (err) {
            console.error('Error loading profile stats:', err);
        }
    };

    const loadAvailability = async (peopleUuid) => {
        if (!peopleUuid) return;

        setLoadingAvailability(true);
        try {
            const data = await getPublicAvailability(peopleUuid);
            setAvailability(data || []);
        } catch (err) {
            console.error('Error loading availability:', err);
            // Don't show error to user, just no availability displayed
        } finally {
            setLoadingAvailability(false);
        }
    };

    const geocodePeopleAddress = async (peopleData) => {
        setGeocoding(true);

        // React 19: Optimistically update UI immediately
        updateOptimisticPeople({
            latitude: 'loading',
            longitude: 'loading'
        });

        try {
            console.log('Geocoding address for people:', peopleData.address);
            const coordinates = await geocodeAddress(peopleData.address);

            // Update coordinates in database
            await updatePeopleCoordinates(peopleData.peopleUuid, coordinates.latitude, coordinates.longitude);

            // Update actual state (optimistic state will automatically sync)
            setPeople(prev => ({
                ...prev,
                latitude: coordinates.latitude,
                longitude: coordinates.longitude
            }));

            console.log('Coordinates updated successfully');
        } catch (err) {
            console.error('Error geocoding address:', err);
            // useOptimistic will automatically rollback on error
        } finally {
            setGeocoding(false);
        }
    };

    const handleBack = () => {
        // Check if we have history to go back to
        // If not (e.g., direct URL access), navigate to dashboard
        if (window.history.length > 1) {
            navigate(-1);
        } else {
            navigate('/');
        }
    };

    // Check if this person is in user's contacts
    const isContactAdded = (peopleId) => {
        if (!contactsLoaded || !userContacts || userContacts.length === 0) return false;
        return userContacts.some(contact => contact.peopleId === peopleId);
    };

    // Get the contact record for this person (to get contact.id for removal)
    const getContactRecord = (peopleId) => {
        if (!userContacts || userContacts.length === 0) return null;
        return userContacts.find(contact => contact.peopleId === peopleId);
    };

    const handleToggleContact = async () => {
        if (!displayPeople || userId <= 0) return;

        const existingContact = getContactRecord(displayPeople.id);

        if (existingContact) {
            // Remove contact using the contact.id (not the people.id)
            const success = await removeUserContact(userId, existingContact.id);
            if (success) {
                // Update local state
                setUserContacts(prev => prev.filter(c => c.id !== existingContact.id));
            }
        } else {
            // Add as contact
            const newContact = await addPeopleAsContact(userId, displayPeople);
            if (newContact) {
                // Update local state
                setUserContacts(prev => [...prev, newContact]);
            }
        }
    };

    // React 19: Use optimisticPeople for display to show instant updates
    const displayPeople = optimisticPeople || people;

    if (loading) {
        return (
            <div className="text-center py-12">
                <div className="w-16 h-16 border-4 border-linkoo-purple border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                <p className="text-gray-600 text-lg">
                    {language === 'fr' ? 'Chargement...' : 'Loading...'}
                </p>
            </div>
        );
    }

    if (error || !displayPeople) {
        return (
            <div className="bg-red-50 border-2 border-red-200 rounded-lg p-6 text-center max-w-md mx-auto">
                <p className="text-red-800 mb-4">
                    {error || (language === 'fr' ? 'Personne non trouvée' : 'Person not found')}
                </p>
                <button
                    onClick={handleBack}
                    className="bg-red-600 text-white px-6 py-2 rounded-lg hover:bg-red-700 transition-colors"
                >
                    {language === 'fr' ? 'Retour' : 'Back'}
                </button>
            </div>
        );
    }

    const isAdded = isContactAdded(displayPeople.id);

    return (
        <div className="bg-white">
            {/* React 19: Document Metadata */}
            {displayPeople && (
                <>
                    <title>{displayPeople.firstName} {displayPeople.lastName} - Linkoo</title>
                    <meta name="description" content={`Contact de ${displayPeople.firstName} ${displayPeople.lastName}${displayPeople.company ? ` - ${displayPeople.company}` : ''}`} />
                </>
            )}

            {/* Back button - Hidden on print */}
            <div className="mb-6 print:hidden">
                <button
                    onClick={handleBack}
                    className="flex items-center text-linkoo-purple hover:text-linkoo-pink transition-colors"
                >
                    <ArrowLeft className="w-5 h-5 mr-2" />
                    {language === 'fr' ? 'Retour' : 'Back'}
                </button>
            </div>

            {/* Card */}
            <div className="bg-white rounded-lg shadow-sm overflow-hidden border border-gray-200">
                {/* Header - Clean and professional */}
                <div className="p-6 sm:p-8 border-b border-gray-100">
                    <div className="flex flex-col sm:flex-row items-start gap-6">
                        {/* Profile Photo */}
                        {profilePhotoUrl ? (
                            <img
                                src={profilePhotoUrl}
                                alt={`${displayPeople.firstName} ${displayPeople.lastName}`}
                                className="w-24 h-24 sm:w-28 sm:h-28 rounded-full object-cover border-2 border-gray-200 flex-shrink-0 mx-auto sm:mx-0"
                            />
                        ) : (
                            <div className="w-24 h-24 sm:w-28 sm:h-28 bg-gray-100 rounded-full flex items-center justify-center border-2 border-gray-200 flex-shrink-0 mx-auto sm:mx-0">
                                <span className="text-3xl sm:text-4xl font-semibold text-gray-500">
                                    {(displayPeople.firstName?.[0] || '') + (displayPeople.lastName?.[0] || '') || '?'}
                                </span>
                            </div>
                        )}

                        <div className="flex-1 min-w-0 text-center sm:text-left">
                            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-1">
                                {displayPeople.firstName} {displayPeople.lastName}
                            </h1>
                            {displayPeople.position && (
                                <p className="text-lg text-gray-600 mb-2">{displayPeople.position}</p>
                            )}
                            {/* Profile Type & Employment Status Badges */}
                            <div className="flex flex-wrap items-center gap-2 justify-center sm:justify-start mb-4">
                                {displayPeople.profileTypeId && PROFILE_TYPES[displayPeople.profileTypeId] && (
                                    <span className={`${PROFILE_TYPES[displayPeople.profileTypeId].color} text-white px-3 py-1 rounded-full text-sm font-medium flex items-center gap-1.5`}>
                                        {React.createElement(PROFILE_TYPES[displayPeople.profileTypeId].icon, { className: 'w-4 h-4' })}
                                        {language === 'fr' ? PROFILE_TYPES[displayPeople.profileTypeId].labelFr : PROFILE_TYPES[displayPeople.profileTypeId].labelEn}
                                    </span>
                                )}
                                {displayPeople.employmentStatus && EMPLOYMENT_STATUS[displayPeople.employmentStatus] && (
                                    <span className={`${EMPLOYMENT_STATUS[displayPeople.employmentStatus].color} text-white px-3 py-1 rounded-full text-sm font-medium flex items-center gap-1.5`}>
                                        {React.createElement(EMPLOYMENT_STATUS[displayPeople.employmentStatus].icon, { className: 'w-4 h-4' })}
                                        {language === 'fr' ? EMPLOYMENT_STATUS[displayPeople.employmentStatus].labelFr : EMPLOYMENT_STATUS[displayPeople.employmentStatus].labelEn}
                                    </span>
                                )}
                            </div>

                            {/* Stats Row */}
                            {profileStats && (
                                <div className="flex flex-wrap gap-4 justify-center sm:justify-start mt-3">
                                    <div className="flex items-center gap-1.5 text-sm text-gray-600">
                                        <Users className="w-4 h-4 text-gray-400" />
                                        <span className="font-medium">{profileStats.contactCount || 0}</span>
                                        <span className="text-gray-400">{language === 'fr' ? 'contacts' : 'contacts'}</span>
                                    </div>
                                    <div className="flex items-center gap-1.5 text-sm text-gray-600">
                                        <CalendarDays className="w-4 h-4 text-gray-400" />
                                        <span className="font-medium">{profileStats.appointmentCount || 0}</span>
                                        <span className="text-gray-400">{language === 'fr' ? 'rdv' : 'appointments'}</span>
                                    </div>
                                    <div className="flex items-center gap-1.5 text-sm text-gray-600">
                                        <Calendar className="w-4 h-4 text-gray-400" />
                                        <span className="font-medium">{profileStats.ownedEventCount || 0}</span>
                                        <span className="text-gray-400">{language === 'fr' ? 'événements' : 'events'}</span>
                                    </div>
                                    <div className="flex items-center gap-1.5 text-sm text-gray-600">
                                        <UserCheck className="w-4 h-4 text-gray-400" />
                                        <span className="font-medium">{profileStats.participatingEventCount || 0}</span>
                                        <span className="text-gray-400">{language === 'fr' ? 'participations' : 'participating'}</span>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Actions - Hidden on print */}
                        <div className="flex flex-col gap-3 flex-shrink-0 print:hidden w-full sm:w-auto">
                            {/* Add/Remove Contact Button - Hidden if viewing own profile */}
                            {displayPeople.peopleUuid !== localStorage.getItem('userUuid') && (
                                <button
                                    onClick={handleToggleContact}
                                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2 ${
                                        isAdded
                                            ? 'bg-green-50 text-green-700 hover:bg-green-100 border border-green-200'
                                            : 'bg-linkoo-purple text-white hover:bg-linkoo-pink'
                                    }`}
                                    title={isAdded ? (language === 'fr' ? 'Retirer de mes contacts' : 'Remove from contacts') : (language === 'fr' ? 'Ajouter aux contacts' : 'Add to contacts')}
                                >
                                    {isAdded ? (
                                        <>
                                            <Check className="w-4 h-4" />
                                            {language === 'fr' ? 'Contact ajouté' : 'Contact added'}
                                        </>
                                    ) : (
                                        <>
                                            <UserPlus className="w-4 h-4" />
                                            {language === 'fr' ? 'Ajouter' : 'Add'}
                                        </>
                                    )}
                                </button>
                            )}

                            {/* Share buttons */}
                            <div className="flex items-center gap-2 justify-center sm:justify-start">
                                <span className="text-xs text-gray-400 mr-1">
                                    <Share2 className="w-4 h-4 inline" />
                                </span>
                                <a
                                    href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="group/share relative p-2 rounded-full bg-gray-100 hover:bg-blue-600 text-gray-600 hover:text-white transition-colors"
                                >
                                    <FacebookIcon />
                                    <span className="absolute -bottom-8 left-1/2 -translate-x-1/2 px-2 py-1 bg-gray-800 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover/share:opacity-100 transition-opacity pointer-events-none">
                                        Partager sur Facebook
                                    </span>
                                </a>
                                <a
                                    href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(window.location.href)}&text=${encodeURIComponent(`${displayPeople.firstName} ${displayPeople.lastName} - Linkoo`)}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="group/share relative p-2 rounded-full bg-gray-100 hover:bg-gray-800 text-gray-600 hover:text-white transition-colors"
                                >
                                    <XIcon />
                                    <span className="absolute -bottom-8 left-1/2 -translate-x-1/2 px-2 py-1 bg-gray-800 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover/share:opacity-100 transition-opacity pointer-events-none">
                                        Partager sur X
                                    </span>
                                </a>
                                <a
                                    href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(window.location.href)}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="group/share relative p-2 rounded-full bg-gray-100 hover:bg-blue-700 text-gray-600 hover:text-white transition-colors"
                                >
                                    <LinkedInIcon />
                                    <span className="absolute -bottom-8 left-1/2 -translate-x-1/2 px-2 py-1 bg-gray-800 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover/share:opacity-100 transition-opacity pointer-events-none">
                                        Partager sur LinkedIn
                                    </span>
                                </a>
                                <a
                                    href={`https://api.whatsapp.com/send?text=${encodeURIComponent(`${displayPeople.firstName} ${displayPeople.lastName} - ${window.location.href}`)}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="group/share relative p-2 rounded-full bg-gray-100 hover:bg-green-500 text-gray-600 hover:text-white transition-colors"
                                >
                                    <WhatsAppIcon />
                                    <span className="absolute -bottom-8 left-1/2 -translate-x-1/2 px-2 py-1 bg-gray-800 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover/share:opacity-100 transition-opacity pointer-events-none">
                                        Partager sur WhatsApp
                                    </span>
                                </a>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Details - 3 columns layout with map */}
                <div className="p-6 sm:p-8">
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                        {/* Column 1 - Contact Info */}
                        <div className="space-y-3">
                            {/* Company */}
                            {displayPeople.company && (
                                <div className="flex items-center gap-3">
                                    <Building2 className="w-5 h-5 text-gray-400 flex-shrink-0" />
                                    <div>
                                        <p className="text-xs text-gray-400 uppercase tracking-wide">
                                            {language === 'fr' ? 'Entreprise' : 'Company'}
                                        </p>
                                        <p className="text-gray-900">{displayPeople.company}</p>
                                    </div>
                                </div>
                            )}

                            {/* Email */}
                            {displayPeople.email && (
                                <div className="flex items-center gap-3">
                                    <Mail className="w-5 h-5 text-gray-400 flex-shrink-0" />
                                    <div>
                                        <p className="text-xs text-gray-400 uppercase tracking-wide">Email</p>
                                        <a
                                            href={`mailto:${displayPeople.email}`}
                                            className="text-gray-900 hover:text-linkoo-purple transition-colors"
                                        >
                                            {displayPeople.email}
                                        </a>
                                    </div>
                                </div>
                            )}

                            {/* Phone */}
                            {displayPeople.phone && (
                                <div className="flex items-center gap-3">
                                    <Phone className="w-5 h-5 text-gray-400 flex-shrink-0" />
                                    <div>
                                        <p className="text-xs text-gray-400 uppercase tracking-wide">
                                            {language === 'fr' ? 'Téléphone' : 'Phone'}
                                        </p>
                                        <a
                                            href={`tel:${displayPeople.phone}`}
                                            className="text-gray-900 hover:text-linkoo-purple transition-colors"
                                        >
                                            {displayPeople.phone}
                                        </a>
                                    </div>
                                </div>
                            )}

                            {/* Website */}
                            {displayPeople.website && (
                                <div className="flex items-center gap-3">
                                    <Globe className="w-5 h-5 text-gray-400 flex-shrink-0" />
                                    <div>
                                        <p className="text-xs text-gray-400 uppercase tracking-wide">
                                            {language === 'fr' ? 'Site web' : 'Website'}
                                        </p>
                                        <a
                                            href={displayPeople.website}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-gray-900 hover:text-linkoo-purple transition-colors break-all"
                                        >
                                            {displayPeople.website}
                                        </a>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Column 2 - Address */}
                        <div>
                            {displayPeople.address && (
                                <div className="flex items-start gap-3">
                                    <MapPin className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
                                    <div>
                                        <p className="text-xs text-gray-400 uppercase tracking-wide">
                                            {language === 'fr' ? 'Adresse' : 'Address'}
                                        </p>
                                        <p className="text-gray-900">{displayPeople.address}</p>
                                    </div>
                                </div>
                            )}
                            {/* Geocoding indicator */}
                            {geocoding && (
                                <div className="mt-3 flex items-center text-sm text-blue-600">
                                    <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mr-2"></div>
                                    {language === 'fr' ? 'Géolocalisation...' : 'Geocoding...'}
                                </div>
                            )}
                        </div>

                        {/* Column 3 - Map */}
                        {optimisticPeople?.latitude && optimisticPeople?.longitude &&
                         optimisticPeople.latitude !== 'loading' && optimisticPeople.longitude !== 'loading' && (
                            <div className="print:hidden">
                                <div className="rounded-lg overflow-hidden border border-gray-200 h-32 lg:h-full lg:min-h-[140px]">
                                    <Map
                                        defaultCenter={{
                                            lat: parseFloat(optimisticPeople.latitude),
                                            lng: parseFloat(optimisticPeople.longitude)
                                        }}
                                        defaultZoom={14}
                                        mapId="linkoo-map"
                                        disableDefaultUI={true}
                                        zoomControl={false}
                                    >
                                        <AdvancedMarker
                                            position={{
                                                lat: parseFloat(optimisticPeople.latitude),
                                                lng: parseFloat(optimisticPeople.longitude)
                                            }}
                                            title={`${optimisticPeople.firstName} ${optimisticPeople.lastName}`}
                                        />
                                    </Map>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Availability Section */}
                    {loadingAvailability ? (
                        <div className="mt-6 bg-gray-50 border-2 border-gray-200 rounded-lg p-6">
                            <div className="flex items-center justify-center">
                                <div className="w-5 h-5 border-2 border-linkoo-purple border-t-transparent rounded-full animate-spin mr-3"></div>
                                <p className="text-gray-600">
                                    {language === 'fr' ? 'Chargement des disponibilités...' : 'Loading availability...'}
                                </p>
                            </div>
                        </div>
                    ) : availability.length > 0 ? (
                        <div className="mt-6">
                            <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                                <Clock className="w-5 h-5 text-linkoo-purple" />
                                {language === 'fr' ? 'Disponibilités' : 'Availability'}
                            </h3>
                            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                                    {DAYS_OF_WEEK.map(day => {
                                        const daySlots = availability.filter(slot => slot.dayOfWeek === day.id);
                                        if (daySlots.length === 0) return null;

                                        return (
                                            <div key={day.id} className="bg-white rounded-lg p-3 shadow-sm border border-gray-100">
                                                <div className="font-medium text-linkoo-purple mb-2 flex items-center gap-2">
                                                    <Calendar className="w-4 h-4" />
                                                    {language === 'fr' ? day.labelFr : day.label}
                                                </div>
                                                <div className="space-y-1">
                                                    {daySlots.map((slot, idx) => (
                                                        <div
                                                            key={idx}
                                                            className="text-sm text-gray-700 bg-green-50 px-2 py-1 rounded flex items-center gap-1"
                                                        >
                                                            <Clock className="w-3 h-3 text-green-600" />
                                                            <span className="font-medium text-green-700">
                                                                {slot.startTime?.substring(0, 5)} - {slot.endTime?.substring(0, 5)}
                                                            </span>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                                <p className="text-xs text-gray-500 mt-3 text-center">
                                    {language === 'fr'
                                        ? 'Ces créneaux indiquent les disponibilités habituelles de ce contact.'
                                        : 'These slots indicate the usual availability of this contact.'}
                                </p>
                            </div>
                        </div>
                    ) : null}

                    {/* Occupations and Skills Section */}
                    {occupations.length > 0 && (
                        <div className="mt-6">
                            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                                <Briefcase className="w-5 h-5 text-linkoo-purple" />
                                {language === 'fr' ? 'Métiers et compétences *' : 'Occupations and skills *'}
                            </h3>
                            <div className="space-y-6">
                                {occupations.map(occupation => {
                                    const skills = occupationSkills[occupation.id] || [];
                                    const selectedSkills = skills.filter(s => userSkillIds.includes(s.id));
                                    const essentialSkills = selectedSkills.filter(s => s.isEssential);
                                    const optionalSkills = selectedSkills.filter(s => !s.isEssential);
                                    const occupationName = occupation[`name_${language}`] || occupation.name_fr || occupation.name_en || '';

                                    return (
                                        <div key={occupation.id} className="border-l-4 border-linkoo-purple pl-4">
                                            <div className="flex items-center gap-2 mb-2">
                                                <h4 className="font-semibold text-gray-900 text-lg">{occupationName}</h4>
                                                {occupation.isPrimary && (
                                                    <span className="text-xs text-linkoo-purple font-medium">
                                                        (Principal)
                                                    </span>
                                                )}
                                            </div>
                                            {selectedSkills.length > 0 ? (
                                                <div className="space-y-2">
                                                    {/* Essential skills */}
                                                    {essentialSkills.length > 0 && (
                                                        <div>
                                                            <p className="text-sm font-medium text-gray-600 mb-1 flex items-center gap-1">
                                                                <Sparkles className="w-3 h-3 text-amber-500" />
                                                                {language === 'fr' ? 'Compétences essentielles :' : 'Essential skills:'}
                                                            </p>
                                                            <ul className="list-disc list-inside text-gray-700 text-sm space-y-0.5 ml-2">
                                                                {essentialSkills.map(skill => {
                                                                    const skillName = skill[`name_${language}`] || skill.name_fr || skill.name_en || '';
                                                                    return <li key={skill.id}>{skillName}</li>;
                                                                })}
                                                            </ul>
                                                        </div>
                                                    )}
                                                    {/* Optional skills */}
                                                    {optionalSkills.length > 0 && (
                                                        <div>
                                                            <p className="text-sm font-medium text-gray-600 mb-1">
                                                                {language === 'fr' ? 'Autres compétences :' : 'Other skills:'}
                                                            </p>
                                                            <ul className="list-disc list-inside text-gray-700 text-sm space-y-0.5 ml-2">
                                                                {optionalSkills.map(skill => {
                                                                    const skillName = skill[`name_${language}`] || skill.name_fr || skill.name_en || '';
                                                                    return <li key={skill.id}>{skillName}</li>;
                                                                })}
                                                            </ul>
                                                        </div>
                                                    )}
                                                </div>
                                            ) : (
                                                <p className="text-sm text-gray-500 italic">
                                                    {language === 'fr' ? 'Aucune compétence sélectionnée' : 'No skills selected'}
                                                </p>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* Enterprises Section */}
                    {enterprises.length > 0 && (
                        <div className="mt-6">
                            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                                <Building2 className="w-5 h-5 text-gray-500" />
                                {language === 'fr' ? 'Entreprises' : 'Companies'}
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {enterprises.map(enterprise => (
                                    <div key={enterprise.id} className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                                        {/* Header: Name + Firm Type */}
                                        <div className="mb-2">
                                            <p className="font-semibold text-gray-900">{enterprise.placeName || enterprise.name}</p>
                                            {enterprise.firmType && (
                                                <p className="text-xs text-linkoo-purple font-medium">{enterprise.firmType}</p>
                                            )}
                                        </div>

                                        {/* Details */}
                                        <div className="space-y-1 text-sm text-gray-600">
                                            {/* TVA */}
                                            {enterprise.tvaNum && (
                                                <p><span className="font-medium text-gray-700">TVA:</span> {enterprise.tvaNum}</p>
                                            )}

                                            {/* Address */}
                                            {enterprise.googleAddress && (
                                                <p className="flex items-start gap-1">
                                                    <MapPin className="w-3.5 h-3.5 mt-0.5 text-gray-400 shrink-0" />
                                                    <span>{enterprise.googleAddress}</span>
                                                </p>
                                            )}

                                            {/* Phone */}
                                            {enterprise.phone && (
                                                <p className="flex items-center gap-1">
                                                    <Phone className="w-3.5 h-3.5 text-gray-400" />
                                                    <a href={`tel:${enterprise.phone}`} className="hover:text-linkoo-purple">
                                                        {enterprise.phone}
                                                    </a>
                                                </p>
                                            )}

                                            {/* Email */}
                                            {enterprise.email && (
                                                <p className="flex items-center gap-1">
                                                    <Mail className="w-3.5 h-3.5 text-gray-400" />
                                                    <a href={`mailto:${enterprise.email}`} className="text-linkoo-purple hover:underline truncate">
                                                        {enterprise.email}
                                                    </a>
                                                </p>
                                            )}

                                            {/* Website */}
                                            {enterprise.website && (
                                                <p className="flex items-center gap-1">
                                                    <Globe className="w-3.5 h-3.5 text-gray-400" />
                                                    <a href={enterprise.website.startsWith('http') ? enterprise.website : `https://${enterprise.website}`}
                                                       target="_blank"
                                                       rel="noopener noreferrer"
                                                       className="text-linkoo-purple hover:underline truncate">
                                                        {enterprise.website.replace(/^https?:\/\//, '')}
                                                    </a>
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Clubs Section */}
                    {clubs.length > 0 && (
                        <div className="mt-6">
                            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                                <Users className="w-5 h-5 text-gray-500" />
                                {language === 'fr' ? 'Clubs et associations' : 'Clubs and associations'}
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {clubs.map(club => (
                                    <div key={club.id} className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                                        {/* Header: Name */}
                                        <div className="mb-2">
                                            <p className="font-semibold text-gray-900">{club.placeName || club.name}</p>
                                        </div>

                                        {/* Details */}
                                        <div className="space-y-1 text-sm text-gray-600">
                                            {/* Address */}
                                            {club.googleAddress && (
                                                <p className="flex items-start gap-1">
                                                    <MapPin className="w-3.5 h-3.5 mt-0.5 text-gray-400 shrink-0" />
                                                    <span>{club.googleAddress}</span>
                                                </p>
                                            )}

                                            {/* Phone */}
                                            {club.phone && (
                                                <p className="flex items-center gap-1">
                                                    <Phone className="w-3.5 h-3.5 text-gray-400" />
                                                    <a href={`tel:${club.phone}`} className="hover:text-linkoo-purple">
                                                        {club.phone}
                                                    </a>
                                                </p>
                                            )}

                                            {/* Email */}
                                            {club.email && (
                                                <p className="flex items-center gap-1">
                                                    <Mail className="w-3.5 h-3.5 text-gray-400" />
                                                    <a href={`mailto:${club.email}`} className="text-linkoo-purple hover:underline truncate">
                                                        {club.email}
                                                    </a>
                                                </p>
                                            )}

                                            {/* Website */}
                                            {club.website && (
                                                <p className="flex items-center gap-1">
                                                    <Globe className="w-3.5 h-3.5 text-gray-400" />
                                                    <a href={club.website.startsWith('http') ? club.website : `https://${club.website}`}
                                                       target="_blank"
                                                       rel="noopener noreferrer"
                                                       className="text-linkoo-purple hover:underline truncate">
                                                        {club.website.replace(/^https?:\/\//, '')}
                                                    </a>
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* ESCO Footer Note */}
                    {occupations.length > 0 && (
                        <div className="mt-8 pt-4 border-t border-gray-100">
                            <p className="text-xs text-gray-400 text-center">
                                * {language === 'fr'
                                    ? 'Classification basée sur le référentiel européen ESCO (European Skills, Competences, Qualifications and Occupations)'
                                    : 'Classification based on the European ESCO framework (European Skills, Competences, Qualifications and Occupations)'}
                            </p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default PeopleDetailContent;
